/**
 * The device's own storage (IndexedDB).
 *
 * Two jobs:
 *   1. Hold a copy of everything the till needs to sell with no internet -
 *      products, barcodes, prices, rates, settings, and recent receipts so
 *      exchanges work offline.
 *   2. Hold the OUTBOX: every sale made on this device that has not reached the
 *      server yet. A sale is written here the instant the cashier presses
 *      Complete, before any network call, so a power cut loses nothing.
 */
import Dexie, { type Table } from 'dexie';
import { compareSizes } from '@pos/shared';
import type { CatalogItem, Currency, Promotion, StoreSettings } from '@pos/shared';
import { localDbName } from './shop.js';

export interface CachedSale {
  id: string;
  receipt_no: string;
  kind: string;
  status: string;
  total: number;
  created_at: string;
  vat_rate: number;
  customer_id?: string | null;
  user_id?: string | null;
}

export interface CachedLine {
  id: string;
  sale_id: string;
  variant_id: string;
  name_en: string;
  name_ar: string;
  size: string;
  color_en?: string;
  color_ar?: string;
  qty: number;
  unit_price: number;
  net: number;
  net_unit: number;
  returned_qty: number;
  final_sale: number;
}

/** One sale waiting to be uploaded. */
export interface Outbox {
  id: string;
  payload: unknown;
  createdAt: string;
  attempts: number;
  lastError?: string | null;
  /** Set when the server refused it for good; it stops retrying and asks a human. */
  stuck?: boolean;
}

export interface KeyVal {
  key: string;
  value: unknown;
}

export class PosDb extends Dexie {
  items!: Table<CatalogItem, string>;
  barcodes!: Table<{ code: string; variantId: string }, string>;
  sales!: Table<CachedSale, string>;
  lines!: Table<CachedLine, string>;
  outbox!: Table<Outbox, string>;
  meta!: Table<KeyVal, string>;

  /** One database per shop, so one shop's outbox can never upload to another. */
  constructor(name = localDbName()) {
    super(name);
    this.version(1).stores({
      items: 'variantId, productId, sku, nameEn, nameAr, categoryId',
      barcodes: 'code, variantId',
      sales: 'id, receipt_no, created_at, customer_id',
      lines: 'id, sale_id, variant_id',
      outbox: 'id, createdAt, stuck',
      meta: 'key',
    });
  }
}

export const db = new PosDb();

// --- the cached catalogue ----------------------------------------------------

export interface Snapshot {
  generatedAt: string;
  settings: StoreSettings;
  currencies: Currency[];
  items: CatalogItem[];
  categories: Record<string, unknown>[];
  brands: Record<string, unknown>[];
  colors: Record<string, unknown>[];
  sizeScales: Record<string, unknown>[];
  users: Record<string, unknown>[];
  recentSales: CachedSale[];
  recentLines: CachedLine[];
  exchangeWindowDays: number;
  promotions?: Promotion[];
}

export async function saveSnapshot(s: Snapshot): Promise<void> {
  await db.transaction('rw', db.items, db.barcodes, db.sales, db.lines, db.meta, async () => {
    await db.items.clear();
    await db.barcodes.clear();
    await db.items.bulkPut(s.items);
    await db.barcodes.bulkPut(
      s.items.flatMap((i) => i.barcodes.map((code) => ({ code, variantId: i.variantId }))),
    );
    // Recent receipts travel with the catalogue so exchanges work offline.
    await db.sales.clear();
    await db.lines.clear();
    await db.sales.bulkPut(s.recentSales ?? []);
    await db.lines.bulkPut(s.recentLines ?? []);
    await setMeta('snapshot', {
      generatedAt: s.generatedAt,
      settings: s.settings,
      currencies: s.currencies,
      categories: s.categories,
      brands: s.brands,
      colors: s.colors,
      sizeScales: s.sizeScales,
      exchangeWindowDays: s.exchangeWindowDays,
      promotions: s.promotions ?? [],
    });
  });
}

export async function getMeta<T>(key: string): Promise<T | null> {
  const row = await db.meta.get(key);
  return (row?.value as T) ?? null;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function findByBarcode(code: string): Promise<CatalogItem | null> {
  const bc = await db.barcodes.get(code);
  if (!bc) return null;
  return (await db.items.get(bc.variantId)) ?? null;
}

/** Search the cached catalogue in both languages, on partial words. */
export interface StyleMatch {
  productId: string;
  nameEn: string;
  nameAr: string;
  /** Every variant of the style, not only the ones that matched the words. */
  variants: CatalogItem[];
  stock: number;
}

/**
 * Search by STYLE rather than by variant.
 *
 * Searching "blazer" used to return every size and colour separately — thirty
 * rows for one garment — and receiving a delivery meant finding each of them
 * in turn. A shopkeeper unpacking a box thinks in styles: one blazer, so many
 * of each size and colour.
 *
 * Two passes on purpose: the first finds which products matched, the second
 * collects ALL of their variants. Matching on colour ("blue blazer") must
 * still bring back the whole grid, or the sizes that did not match the words
 * would be missing from it.
 */
export async function searchStyles(q: string, limit = 20): Promise<StyleMatch[]> {
  const needle = q.trim().toLowerCase();
  const matched = new Set<string>();
  const byProduct = new Map<string, CatalogItem[]>();

  await db.items.each((i) => {
    const list = byProduct.get(i.productId);
    if (list) list.push(i);
    else byProduct.set(i.productId, [i]);

    if (matched.size >= limit && !matched.has(i.productId)) return;
    const hay = `${i.nameEn} ${i.nameAr} ${i.sku} ${i.size} ${i.colorEn} ${i.colorAr} ${i.categoryEn ?? ''} ${i.categoryAr ?? ''} ${i.brandEn ?? ''} ${i.brandAr ?? ''} ${i.barcodes.join(' ')}`.toLowerCase();
    if (!needle || hay.includes(needle)) matched.add(i.productId);
  });

  const out: StyleMatch[] = [];
  for (const productId of matched) {
    const variants = byProduct.get(productId);
    if (!variants || variants.length === 0) continue;
    const first = variants[0]!;
    // Size order first, then colour: the grid reads S, M, L down the side
    // instead of the alphabetical L, M, S the database hands back.
    const ordered = [...variants].sort(
      (a, b) => compareSizes(a.size, b.size) || a.colorEn.localeCompare(b.colorEn),
    );
    out.push({
      productId,
      nameEn: first.nameEn,
      nameAr: first.nameAr,
      variants: ordered,
      stock: variants.reduce((a, v) => a + (v.stock ?? 0), 0),
    });
    if (out.length >= limit) break;
  }
  out.sort((a, b) => (a.nameEn || a.nameAr).localeCompare(b.nameEn || b.nameAr));
  return out;
}

export async function searchItems(q: string, limit = 40): Promise<CatalogItem[]> {
  const needle = q.trim().toLowerCase();
  if (!needle) return db.items.limit(limit).toArray();

  const out: CatalogItem[] = [];
  await db.items.each((i) => {
    if (out.length >= limit * 3) return;
    const hay = `${i.nameEn} ${i.nameAr} ${i.sku} ${i.size} ${i.colorEn} ${i.colorAr} ${i.categoryEn ?? ''} ${i.categoryAr ?? ''} ${i.barcodes.join(' ')}`.toLowerCase();
    if (hay.includes(needle)) out.push(i);
  });
  return out.slice(0, limit);
}

/**
 * Keep a completed sale in the device's own cache straight away.
 *
 * The snapshot only carries what the SERVER knows, so without this a sale made
 * during an outage could not be exchanged until the connection came back - and
 * same-day returns are the most common kind.
 */
export async function cacheCompletedSale(sale: CachedSale, lines: CachedLine[]): Promise<void> {
  await db.transaction('rw', db.sales, db.lines, async () => {
    await db.sales.put(sale);
    await db.lines.bulkPut(lines);
  });
}

/** Mark pieces as returned on the cached copy, so the till will not allow it twice. */
export async function markCachedReturned(saleLineId: string, qty: number): Promise<void> {
  const line = await db.lines.get(saleLineId);
  if (line) await db.lines.update(saleLineId, { returned_qty: (line.returned_qty ?? 0) + qty });
}

export async function cachedSaleByReceipt(receiptNo: string) {
  const sale =
    (await db.sales.where('receipt_no').equals(receiptNo.toUpperCase()).first()) ??
    (await db.sales.get(receiptNo));
  if (!sale) return null;
  const lines = await db.lines.where('sale_id').equals(sale.id).toArray();
  return { sale, lines };
}

// --- the outbox --------------------------------------------------------------

export async function queueSale(id: string, payload: unknown): Promise<void> {
  await db.outbox.put({ id, payload, createdAt: new Date().toISOString(), attempts: 0 });
}

export async function outboxCount(): Promise<number> {
  // Not a where() on `stuck`: rows that are fine leave it undefined, and Dexie
  // does not index undefined, so an index query would quietly miss all of them.
  return db.outbox.filter((x) => !x.stuck).count();
}

export async function oldestQueued(): Promise<string | null> {
  const first = await db.outbox.orderBy('createdAt').first();
  return first?.createdAt ?? null;
}

/** Wipe everything on this device. Used when signing out of a shared tablet. */
export async function clearDevice(): Promise<void> {
  await db.delete();
}
